/// <amd-module name="typeahead/core/ExpediaSuggestServiceRequestGenerator" />
/// <amd-dependency path="jquery" name="$"/>

/// <reference path="../../typings/typeahead/browser.d.ts" />
/// <reference path="../../typings/typeahead/ExpediaSuggestServiceRequestGenerator.d.ts" />
/// <reference path="../../typings/typeahead/modulizr.d.ts" />
/// <reference path="../../typings/typeahead/oip.d.ts" />
/// <reference path="../../typings/typeahead/plugins.d.ts" />
/// <reference path="../../typings/typeahead/window.d.ts" />

"use strict";
import {RequestGenerator} from "typeahead/core/RequestGenerator";
import {ISuggestionData} from "typeahead/core/Suggestion";

export interface IInputElementConfig {
    inputElementId: string;
    searchRegionId: string;
}

function getOptionalServiceUrl(options: any, srchText: string): string {
    return [
        options.additionalDataSourceUrl,
        encodeURIComponent(srchText),
    ].join("");
}

let optInPreference: IOptInPreference = {
    canTrack: false,
    no: false,
    set: false,
    source: "meta",
    yes: false,
};

// Disable rule because importing oip using ES2015-style import will make the loading of this module depends on oip,
// which may not be always available.
// tslint:disable-next-line:no-var-requires
require(["oip"], (oip: IOptInPreference) => {
    optInPreference = oip;
});

function getQueryParams(requestParams: ISuggestServiceQueryParams, context: IContext): any {
    let urlParams: any = {};

    // I think it makes sense to split the list of parameters into 2 groups
    // - parameters come from the context
    // - parameters come from the request params object that may or may not have features/tests

    // parameters from the context
    urlParams.client = context.clientId;
    urlParams.siteid = context.siteid;
    urlParams.guid = context.guid;
    urlParams.lob = context.lob;
    urlParams.locale = context.locale;
    urlParams.expuserid = context.expuserid;

    // parameters from the request params object
    urlParams.regiontype = requestParams.mask;
    urlParams.ab = requestParams.ab; // aboverrides should be provided as string eg: "1234.0" or "1234.0|2345.1"
    urlParams.dest = requestParams.dest;
    urlParams.maxresults = requestParams.maxresults;
    urlParams.features = requestParams.features.join("|");
    urlParams.rid = requestParams.rid;
    urlParams.sortcriteria = requestParams.sortcriteria;
    urlParams.categories = requestParams.categories;
    urlParams.pt = requestParams.packageType;
    urlParams.originRegionId = requestParams.originRegionId;
    urlParams.originSearchString = requestParams.originSearchString;
    urlParams.next = requestParams.next;

    // others
    urlParams.format = "jsonp";
    urlParams.device = requestParams.device;
    urlParams.browser = requestParams.browser;
    urlParams.personalize = !!(optInPreference.canTrack && requestParams.personalize);

    return urlParams;
}

function constructRequestParams(options: any): ISuggestServiceQueryParams {
    let requestParams: ISuggestServiceQueryParams = <ISuggestServiceQueryParams> {};

    requestParams.mask = options.mask;
    requestParams.ab = options.abtest;
    requestParams.format = options.format;
    requestParams.dest = options.dest;
    requestParams.maxresults = options.maxresults;
    requestParams.features = options.features || [];
    // device_type is available little late after pageload
    requestParams.device = window.utag_data ? window.utag_data.device_type : "Unavailable";
    requestParams.browser = options.browser;
    requestParams.rid = options.filter_region_id;
    requestParams.personalize = options.personalize;
    requestParams.packageType = options.packageType;

    return requestParams;
}

function getApplicableFeatureFlag (id: string): string[] {
    let featureFlagConfigMap: {[index: string]: string[]} = {
        CARS : ["postal_code"],
        HOTELS : ["contextual_ta", "postal_code"],
        NONE : ["unspecified_area"],
    };

    return featureFlagConfigMap[id];
}
function applyDefaultRequestParamModifiers(context: IContext,
                                           parameters: ISuggestServiceQueryParams,
                                           options: IInputElementConfig): ISuggestServiceQueryParams {
    let applicableFeatureFlag: string[] = [];
    parameters.features = parameters.features && parameters.features.length ? parameters.features || [] : [];

    if (context.tab === "hotel" || context.tab === "flight") {
        parameters.personalize = true;
    }

    if (context.lob === "HOTELS") {
        applicableFeatureFlag = getApplicableFeatureFlag("HOTELS");
        const postalCodeHotelBits = 32; // (postalCodeHotelBits >>> 0).toString(2) = 100000
        /* tslint:disable-next-line: no-bitwise */
        parameters.mask |= postalCodeHotelBits;
    } else if (context.lob === "CARS") {
        applicableFeatureFlag = getApplicableFeatureFlag("CARS");
    } else if (context.lob === "NONE") {
        applicableFeatureFlag = getApplicableFeatureFlag("NONE");
    }

    if (context.clientId === "Hotels.Search") {
        parameters.rid = options.searchRegionId;
    }

    for (let feature of applicableFeatureFlag) {
        if (parameters.features.indexOf(feature) < 0) {
            parameters.features.push(feature);
        }
    }
    return parameters;
}

function applyOriginRequestParamModifiers(parameters: ISuggestServiceQueryParams, options: any):
                                                                                        ISuggestServiceQueryParams {
    if (options.pairedinputid) {
        let $pairedInput: JQuery = $("#" + options.pairedinputid);
        if ($pairedInput.length > 0) {
            let taJsonElement: string = $pairedInput.attr("data-ta_element");
            let $taJsonElement: JQuery = $("#" + taJsonElement);
            if ($taJsonElement.length > 0) { // Homepage
                try {
                    let taJson: ISuggestionData = JSON.parse($taJsonElement.val());
                    parameters.originRegionId = taJson.regionId;
                } catch (e) { /* Ignore */ }
            } else if ($pairedInput.attr("data-id")) { // Results Page
                parameters.originRegionId = $pairedInput.attr("data-id");
            }
            parameters.originSearchString = $pairedInput.val();
        }
    }
    return parameters;
}

function applyRequestParamModifiers(
         plugins: IRequestUrlParamModifier[],
         context: IContext,
         parameters: ISuggestServiceQueryParams,
         options: IInputElementConfig): ISuggestServiceQueryParams {
    parameters = applyDefaultRequestParamModifiers(context, parameters, options);
    parameters = applyOriginRequestParamModifiers(parameters, options);
    if (plugins.length > 0) {
        plugins.map((plugin: IRequestUrlParamModifier) => {
            return plugin.modifyParameters(parameters, context, options);
        });
    }
    return parameters;
}

/** Basic hash function (from Java) for converting a string to integer with range [0, 2^31 - 1]
 * @param text: string
 * @returns {number}
 */
export function hash (text: string): number {
    let hash: number = 0;
    if (text && text.length !== 0) {
        let charCode: number;
        for (let i: number = 0; i < text.length; i++) {
            charCode = text.charCodeAt(i);
            /* tslint:disable-next-line: no-bitwise */
            hash  = ((hash << 5) - hash) + charCode;
            /* tslint:disable-next-line: no-bitwise */
            hash |= 0; // Convert to 32bit integer
        }
        // ensure hash is positive
        if (hash < 0) {
            return (hash + 1) * -1;
        }
    }
    return hash;
}

export class ExpediaSuggestServiceRequestGenerator extends RequestGenerator {
    private static readonly sessionId: number = Date.now();

    constructor(essAbacusIds: string[], variantAssigner: (id?: string) => string,
                suggestServiceUrlOptions: ISuggestServiceUrlOptions, siteid: string, pluginAssembly: IPluginAssembly) {
        super(essAbacusIds, variantAssigner, suggestServiceUrlOptions, siteid, pluginAssembly);
    }

    public buildQueryStringParams(srchText: string, options: any, context: IContext): any {
        let requestParams: ISuggestServiceQueryParams = constructRequestParams(options);
        let inputOptions: IInputElementConfig = <IInputElementConfig> $.extend({}, options);
        let params = applyRequestParamModifiers(this.requestParamModifiers, context, requestParams, inputOptions);
        return getQueryParams(params, context);
    }

    public makeRequest(srchText: string, options: any, context: IContext): JQueryPromise<any> {
        let url;
        if (options.isSecondary) {
            url = getOptionalServiceUrl(options, srchText);
        } else {
            url = this.constructEssUrl(srchText);
        }

        return $.ajax({
            beforeSend: (jqXHR, settings) => {
                window.utaTestHooks.latestEssRequest = settings.url;
            },
            cache: true,
            data: this.buildQueryStringParams(srchText, options, context),
            dataType: "jsonp",
            jsonpCallback: "cb_" + ExpediaSuggestServiceRequestGenerator.sessionId + "_" + hash(srchText),
            type: "GET",
            url,
        });
    }
}

/**
 * Function to allow the caller to provide the version of jQuery they want to use
 * and not rely on the page load time AMD module loader to provide one. This facilitates
 * unit testing so you can mock/stub/fake the jQuery functionality used in here.
 * @param jQuery
 */
export function overrideJQuery(jQuery: JQueryStatic): void {
    $ = jQuery;
}
